import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Card } from '@radix-ui/themes'
import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import PageSkeleton from '../skeleton/PageSkeleton.tsx'
import { Pagination } from '../pagination/Pagination.tsx'
import { travelEventsColumns } from './TravelColumns.tsx'
import { fetchTravelEvents } from '../../reducers/travel/travel.thunk.ts'
import {
  selectTravel,
  setTravelEventsFilters,
  setTravelEventsTableCurrentPage,
} from '../../reducers/travel/travel.slice.ts'
import { TravelEvent, TravelLocation, TravelRisk } from '../../types/TravelTypes.ts'
import { PaginationSelectOption } from '../../types/MemberTypes.ts'
import { SelectOption } from '../../types/DropDownTypes.ts'
import DropDown from '../dropdown/DropDown.tsx'
import { travelRiskOptions } from '../../strings/dropdown-options.ts'
import { countryNameToCode } from '../../utils/malware-mapper.ts'
import Map from '../charts/Map.tsx'
import { IconMapPin } from '@tabler/icons-react'
import { selectUser } from '../../reducers/users/users.slice.ts'
import { useNavigate } from 'react-router-dom'
import { inPov } from '../../utils/session.ts'
import { HEADER_HEIGHT_WITH_TRIAL, HEADER_HEIGHT_WITHOUT_TRIAL } from '../../strings/consts.ts'
import { Input } from '../input/Input.tsx'
import TravelTable from './TravelTable.tsx'

function TravelWrapper() {
  const {
    travelEvents,
    mapLocations,
    travelLocationOptions,
    eventsTableCurrentPage,
    travelEventsTotalPages,
    eventsFilters,
  } = useSelector(selectTravel)
  const di = useAppDispatch()
  const dispatch = useDispatch()
  const [dataLoading, setDataLoading] = useState<boolean>(true)
  const [selectedRisk, setRiskFilter] = useState<SelectOption[]>([])
  const [selectedCountries, setCountryFilter] = useState<SelectOption[]>([])
  const [fromDateTime, setFromDateTime] = useState<string>('')
  const [toDateTime, setToDateTime] = useState<string>('')
  const [countryOptions, setCountryOptions] = useState<SelectOption[]>([])
  const [csvData, setCsvData] = useState<string>('')
  const [mapData, setMapData] = useState<object>({})
  const defaultSortOrder = 'desc'
  const defaultSortColumn = 'eventTime'
  const [sortOrder, setSortOrder] = useState<string>(defaultSortOrder)
  const [sortColumn, setSortColumn] = useState<string>(defaultSortColumn)
  const { session, darkModeEnabled } = useSelector(selectUser)
  const navigate = useNavigate()

  useEffect(() => {
    if (session?.portalPreferences?.sensitiveTravel == false) {
      navigate('/dashboard')
    }
  }, [session])

  useEffect(() => {
    async function loadMapData() {
      try {
        const response = await fetch('https://code.highcharts.com/mapdata/custom/world.topo.json')
        const topology = await response.json()
        setMapData(topology)
      } catch (error) {
        console.error('Error loading map data:', error)
      }
    }

    loadMapData()
  }, [])

  useEffect(() => {}, [csvData])

  useEffect(() => {
    if (travelLocationOptions) {
      setCountryOptions(travelLocationOptions)
    }
  }, [travelLocationOptions])

  useEffect(() => {
    mapTravelData(mapLocations)
  }, [mapLocations])

  const mapTravelData = (data: TravelLocation[]) => {
    let csvString = 'Code,Value,Color\n'
    data.forEach((item) => {
      const countryCode = countryNameToCode[item.country] || 'N/A'
      if (countryCode !== 'N/A') {
        csvString += `${countryCode},${item.deviceCount},${mapColorAndApplyOpacity(item.riskLevel)}\n`
      }
    })
    setCsvData(csvString)
  }

  useEffect(() => {
    di(fetchTravelEvents(eventsFilters, eventsTableCurrentPage, sortColumn, sortOrder))
    setRiskFilter(eventsFilters.risk)
    setCountryFilter(eventsFilters.countries)
    setFromDateTime(eventsFilters.fromDateTime || '')
    setToDateTime(eventsFilters.toDateTime || '')
  }, [eventsFilters, sortColumn, sortOrder])

  useEffect(() => {
    if (travelEvents || (travelEvents && (travelEvents as TravelEvent[])?.length === 0)) {
      setDataLoading(false)
    } else if (!travelEvents) {
      setDataLoading(true)
    }
  }, [travelEvents])

  const handlePageClick = (data: PaginationSelectOption) => {
    setDataLoading(true)
    di(fetchTravelEvents({ ...eventsFilters }, data.selected, sortColumn, sortOrder))
  }

  const resetFilters = () => {
    dispatch(setTravelEventsTableCurrentPage(0))
    dispatch(setTravelEventsFilters({ countries: [], risk: [], fromDateTime: '', toDateTime: '' }))
  }

  const updateRiskFilter = (selectedRisk: SelectOption[]) => {
    dispatch(setTravelEventsTableCurrentPage(0))
    dispatch(setTravelEventsFilters({ ...eventsFilters, risk: selectedRisk }))
  }

  const updateCountryFilter = (selectedCountries: SelectOption[]) => {
    dispatch(setTravelEventsTableCurrentPage(0))
    dispatch(setTravelEventsFilters({ ...eventsFilters, countries: selectedCountries }))
  }

  const updateFromDateTime = (fromDateTime: string) => {
    dispatch(setTravelEventsTableCurrentPage(0))
    dispatch(setTravelEventsFilters({ ...eventsFilters, fromDateTime }))
  }

  const updateToDateTime = (toDateTime: string) => {
    dispatch(setTravelEventsTableCurrentPage(0))
    dispatch(setTravelEventsFilters({ ...eventsFilters, toDateTime }))
  }

  const sortBy = (column: string) => {
    if (sortColumn === column) {
      if (sortOrder === 'desc' && column !== defaultSortColumn) {
        // Clear Sort
        setSortColumn(defaultSortColumn)
        setSortOrder(defaultSortOrder)
      } else {
        // Sort Existing Column
        setSortOrder(sortOrder == 'desc' ? 'asc' : 'desc')
      }
    } else {
      // Sort New Column
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const mapColorAndApplyOpacity = (risk: TravelRisk) => {
    const color = travelRiskOptions.find((option) => option.value === risk)?.color
    const hex = (darkModeEnabled ? color?.dark : color?.light) || '#00000010'
    const cleanHex = hex.replace('#', '')
    const bigint = parseInt(cleanHex, 16)
    const r = (bigint >> 16) & 255
    const g = (bigint >> 8) & 255
    const b = bigint & 255
    return `"rgba(${r}, ${g}, ${b}, 0.6)"`
  }

  return (
    <>
      <div
        className="absolute top-18 left-52 rounded-tl-lg border-mainBorder border-t border-l w-[calc(100%_-_208px)] bg-contentBackground p-9 overflow-auto"
        style={{
          height: inPov(session)
            ? `calc(100% - ${HEADER_HEIGHT_WITH_TRIAL})`
            : `calc(100% - ${HEADER_HEIGHT_WITHOUT_TRIAL})`,
        }}
      >
        {dataLoading ? (
          <div className="flex justify-center items-center min-h-screen">
            <PageSkeleton />
          </div>
        ) : travelEvents && travelEvents?.length > 0 || mapLocations?.length > 0 ? (
          <div className="flex flex-col gap-8 h-full">
            <Card className="p-0 m-0 bg-cardFill border border-mainBorder min-h-[300px]">
              <Map height="300px" chartData={csvData} mapData={mapData} tooltipTitle="Members" />
            </Card>
            <div className="flex justify-between">
              <div className="w-full gap-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
                <DropDown
                  value={selectedCountries}
                  onChangeHandler={updateCountryFilter}
                  options={countryOptions}
                  style="z-10 h-8 min-w-[160px]"
                  placeholder="Country"
                  multiple={true}
                  disabled={false}
                  height="auto"
                />
                <DropDown
                  value={selectedRisk}
                  onChangeHandler={updateRiskFilter}
                  options={travelRiskOptions}
                  style="z-10 h-8 min-w-[160px]"
                  placeholder="Risk"
                  multiple={true}
                  disabled={false}
                  height="auto"
                />
                <div className="flex items-center">
                  <label className="uppercase text-neutral-11 dark:text-neutral-6 font-medium tracking-widest flex items-center text-sm pr-1">
                    From
                  </label>
                  <Input
                    value={fromDateTime}
                    onChange={(e) => {
                      updateFromDateTime(e)
                    }}
                    type="datetime-local"
                  />
                </div>
                <div className="flex items-center">
                  <label className="uppercase text-neutral-11 dark:text-neutral-6 font-medium tracking-widest flex items-center text-sm pr-1">
                    To
                  </label>
                  <Input
                    value={toDateTime}
                    onChange={(e) => {
                      updateToDateTime(e)
                    }}
                    type="datetime-local"
                  />
                </div>
              </div>
            </div>
            {travelEvents && travelEvents.length > 0 && (
              <>
                <TravelTable
                  columns={travelEventsColumns()}
                  data={travelEvents}
                  loading={dataLoading}
                  sortColumn={sortColumn}
                  sortOrder={sortOrder}
                  sortFunction={(column: string) => {
                    sortBy(column)
                  }}
                />
                <div className="mt-2">
                  <Pagination
                    totalPages={travelEventsTotalPages}
                    forcePage={eventsTableCurrentPage}
                    handlePageClick={handlePageClick}
                  />
                </div>
              </>
            )}
            {travelEvents && travelEvents.length === 0 && (
              <div className="block items-center mt-20">
                <div className="flex items-center justify-center">
                  <MagnifyingGlassIcon className="h-12 w-12" />
                </div>
                <p className="text-center font-bold">No Results Found</p>
                <Button
                  className="mt-4 text-center flex bg-black mx-auto outline-1 dark:outline dark:outline-white"
                  onClick={() => {
                    resetFilters()
                  }}
                >
                  Clear all filters
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full pb-20 text-center gap-3">
            <IconMapPin className="h-6 w-6 text-PurpleBlue-9" />
            <h1 className="font-semibold text-2xl">No Data Available</h1>
            <p>We have been unable to detect any devices travelling.</p>
          </div>
        )}
      </div>
    </>
  )
}

export default TravelWrapper
